import React, { useState, useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";
import { useNavigate } from "react-router-dom"; // Add this import
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Divider,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import QrCodeIcon from "@mui/icons-material/QrCode";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import RefreshIcon from "@mui/icons-material/Refresh";
import TimerIcon from "@mui/icons-material/Timer";

// Import your hooks
import { useGenerateLaoQr } from "../../hooks/useLaoQr";
import { LaoQrModel, SuccessData } from "../../types/payment";
import { SOCKET_URL, socketOptions } from "../../constants/apiRoutes";
import { getOrderStatusManual } from "../../utils/order.utils";
import { PaymentSocketResponse } from "../../types/payment";

// Types
interface LaoQrPaymentProps {
  paymentChannelId: string;
  orderId: string;
  cusId: string;
  onPaymentSuccess: (data: any) => void;
  onPaymentError: (error: string) => void;
  onCancel: () => void;
  autoNavigateOnSuccess?: boolean; // Optional prop to control auto navigation
  navigationDelay?: number; // Optional delay before navigation (in milliseconds)
}

// Payment status enum
enum PaymentStatus {
  GENERATING = "generating",
  PENDING = "pending",
  SUCCESS = "success",
  FAILED = "failed",
  EXPIRED = "expired",
}

// Fixed, high-resolution QR request — keeps the code crisp regardless of the
// rendered size (we let CSS scale it down to fit the box).
const QR_RENDER_PX = 320;

// Styled components
const QrCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const QrCodeBox = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 220,
  aspectRatio: "1",
  backgroundColor: "#fff",
  margin: "0 auto",
  borderRadius: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    maxWidth: 200,
  },
}));

const QrWithIconContainer = styled(Box)(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const CenterIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "white",
  padding: theme.spacing(0.25),
  borderRadius: theme.spacing(0.5),
  boxShadow: theme.shadows[1],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1,
  width: 30,
  height: 30,
}));

const ResponsiveQrImage = styled("img")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  display: "block",
}));

const LaoQrPayment: React.FC<LaoQrPaymentProps> = ({
  paymentChannelId,
  orderId,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const [qrData, setQrData] = useState<LaoQrModel | null>(null);
  const [status, setStatus] = useState<PaymentStatus>(PaymentStatus.GENERATING);
  const [error, setError] = useState<string>("");
  const [socketConnected, setSocketConnected] = useState<boolean>(false);
  const navigate = useNavigate();

  // Hooks
  const { mutate: generateQr, isPending: isGenerating } = useGenerateLaoQr();

  // Refs
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = useState<number>(300); // 5 minutes
  // const navigationInterval = useRef<NodeJS.Timeout | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Countdown timer
  useEffect(() => {
    if (status === PaymentStatus.PENDING && countdown > 0) {
      countdownInterval.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setStatus(PaymentStatus.EXPIRED);
            setError("Payment session expired. Please try again.");
            cleanup();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [status, countdown]);

  // // Navigation countdown timer
  // useEffect(() => {
  //   if (
  //     status === PaymentStatus.SUCCESS &&
  //     autoNavigateOnSuccess &&
  //     navigatingCountdown > 0
  //   ) {
  //     navigationInterval.current = setInterval(() => {
  //       setNavigatingCountdown((prev) => {
  //         if (prev <= 1) {
  //           navigateToOrderHistory();
  //           return 0;
  //         }
  //         return prev - 1;
  //       });
  //     }, 1000);
  //   }

  //   return () => {
  //     if (navigationInterval.current) {
  //       clearInterval(navigationInterval.current);
  //     }
  //   };
  // }, [status, navigatingCountdown, autoNavigateOnSuccess]);

  const initializePayment = async () => {
    try {
      setStatus(PaymentStatus.GENERATING);
      setError("");

      // Generate QR code using your API
      generateQr(
        { paymentChannelId, orderId },
        {
          onSuccess: (qrResult) => {
            setQrData(qrResult);
            setStatus(PaymentStatus.PENDING);

            // Start WebSocket monitoring
            attachSocket(orderId);
          },
          onError: (err: any) => {
            console.error("❌ QR generation failed:", err);
            setError(err.message || "Failed to generate QR code");
            setStatus(PaymentStatus.FAILED);
            onPaymentError(err.message || "Failed to generate QR code");
          },
        },
      );
    } catch (err: any) {
      setError(err.message || "Failed to initialize payment");
      setStatus(PaymentStatus.FAILED);
      onPaymentError(err.message || "Failed to initialize payment");
    }
  };

  // WebSocket monitoring
  const attachSocket = (orderId: string) => {
    if (!socketRef.current) {
      socketRef.current = io(SOCKET_URL, { ...socketOptions });
    }

    const s = socketRef.current;

    // @ts-ignore detach previous listeners
    s._detach?.();

    const onConnect = () => {
      setSocketConnected(true);
      s.emit("subscribe", { orderId });
    };
    const onOrderUpdate = (data: PaymentSocketResponse) => {
      const status = getOrderStatusManual(data);

      if (status === "success") {
        setStatus(PaymentStatus.SUCCESS);

        onPaymentSuccess({ ...data, paymentMethodaData: { laoQr: true } });
        // }
        return;
      }

      if (status === "failed") {
        setStatus(PaymentStatus.FAILED);
        onPaymentError(data?.message || data?.desc_payment || "Payment failed");
        return;
      }
      setStatus(PaymentStatus.PENDING);

      // don't hard-fail the UX; backend may still complete and later reconnect
    };

    // handle sokcet errors
    const onConnectError = (err: any) => {
      setSocketConnected(false);
      console.error("Failed to establish Socket connection:", error);
      setError("Failed to establish real-time connection");
    };

    const onDisconnect = () => {
      setSocketConnected(false);
    };

    s.on("connect", onConnect);
    s.on(orderId, onOrderUpdate);
    s.on("connect_error", onConnectError);
    s.on("disconnect", onDisconnect);

    // @ts-ignore
    s._detach = () => {
      s.off("connect", onConnect);
      s.off(orderId, onOrderUpdate);
      s.off("connect_error", onConnectError);
      s.off("disconnect", onDisconnect);
    };
  };

  // replace with centralized navigation function or can click only if success
  const navigateToOrderHistory = () => {
    navigate(`/order/${orderId}`);
  };

  const detachSocket = () => {
    const s = socketRef.current;
    if (s) {
      // @ts-ignore
      s._detach?.();
      s.disconnect();
      socketRef.current = null;
      setSocketConnected(false);
    }
  };
  const cleanup = () => {
    detachSocket();
    // Clear countdown interval
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
  };

  const handleRetry = () => {
    cleanup();
    setCountdown(300);
    // setNavigatingCountdown(0);
    initializePayment();
  };

  const handleViewOrder = () => {
    navigateToOrderHistory();
  };

  // Initialize payment
  useEffect(() => {
    // // Attach socket when component mounts or orderId changes
    initializePayment();
    return () => {
      // Detach socket when component unmounts or orderId changes
      detachSocket();
    };
  }, [orderId]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getStatusColor = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.SUCCESS:
        return "success";
      case PaymentStatus.FAILED:
      case PaymentStatus.EXPIRED:
        return "error";
      case PaymentStatus.PENDING:
        return "warning";
      default:
        return "info";
    }
  };

  const getStatusText = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.GENERATING:
        return "Generating QR Code...";
      case PaymentStatus.PENDING:
        return "Waiting for Payment";
      case PaymentStatus.SUCCESS:
        return "Payment Successful";
      case PaymentStatus.FAILED:
        return "Payment Failed";
      case PaymentStatus.EXPIRED:
        return "Payment Expired";
      default:
        return "Processing...";
    }
  };

  const getConnectionText = () => {
    return socketConnected ? "Live" : "Reconnecting…";
  };

  const statusColor = `${getStatusColor(status)}.main`;
  const isBusy = status === PaymentStatus.GENERATING || isGenerating;

  return (
    <Box sx={{ maxWidth: 420, mx: "auto", width: "100%" }}>
      {/* Header: title + subtle live-connection dot (matches card details) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <QrCodeIcon sx={{ fontSize: 20, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight={700}>
            Lao QR Payment
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: socketConnected ? "success.main" : "text.disabled",
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {getConnectionText()}
          </Typography>
        </Box>
      </Box>

      <QrCard elevation={0}>
        {/* Inline status — small dot + label, same language as the card form */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: 2,
          }}
        >
          {isBusy ? (
            <CircularProgress size={10} />
          ) : (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: statusColor,
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{ color: statusColor, fontWeight: 600 }}
          >
            {getStatusText(status)}
          </Typography>
        </Box>

        <QrCodeBox>
          {isBusy ? (
            <CircularProgress size={48} />
          ) : status === PaymentStatus.SUCCESS ? (
            <Fade in>
              <CheckCircleIcon sx={{ fontSize: 64, color: "success.main" }} />
            </Fade>
          ) : status === PaymentStatus.FAILED ||
            status === PaymentStatus.EXPIRED ? (
            <Fade in>
              <ErrorIcon sx={{ fontSize: 64, color: "error.main" }} />
            </Fade>
          ) : qrData ? (
            <QrWithIconContainer>
              <ResponsiveQrImage
                src={`https://api.qrserver.com/v1/create-qr-code/?size=${QR_RENDER_PX}x${QR_RENDER_PX}&margin=0&data=${encodeURIComponent(
                  qrData.emv,
                )}`}
                alt="Payment QR Code"
              />
              <CenterIcon>
                <img
                  src="laoQrIcon.png"
                  alt="Lao QR"
                  style={{ width: "100%", height: "100%" }}
                />
              </CenterIcon>
            </QrWithIconContainer>
          ) : null}
        </QrCodeBox>

        {status === PaymentStatus.PENDING && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Scan with your banking app to pay
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 1,
                gap: 0.75,
              }}
            >
              <TimerIcon sx={{ fontSize: 16, color: "warning.main" }} />
              <Typography
                variant="caption"
                color="warning.main"
                fontWeight={600}
              >
                {formatTime(countdown)} remaining
              </Typography>
            </Box>
          </Box>
        )}

        {qrData && (
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", wordBreak: "break-all" }}
            >
              Ref: {qrData.payment_ref}
            </Typography>
            {qrData.qr_expiry && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.25 }}
              >
                Expires in {qrData.qr_expiry}
              </Typography>
            )}
          </Box>
        )}
      </QrCard>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        {(status === PaymentStatus.FAILED ||
          status === PaymentStatus.EXPIRED) && (
          <Button
            variant="contained"
            fullWidth
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            sx={{ mb: 0.5 }}
          >
            Try Again
          </Button>
        )}

        {status === PaymentStatus.SUCCESS && (
          <Button
            variant="contained"
            color="success"
            fullWidth
            onClick={handleViewOrder}
          >
            View Order Details
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default LaoQrPayment;
